'use client';

import {useEffect} from 'react';

type Props = {
  /** класс который вешается на все наблюдаемые элементы сразу (например, чтобы задать дефолтную длину транзишенов) */
  initialClassName: string;
  /** класс который вешается на наблюдаемый элемент когда он за пределами экрана */
  offsetClassName: string;
  /** класс который вешается на наблюдаемый элемент когда он в пределах экрана */
  defaultClassName: string;
};

/**
 * Компонент, анимирующий элементы по скроллу
 *
 * как вписаться: на элементы, которые хотим анимировать, поставить атрибут **data-prx-by**,
 *  в значение вписать айди элемента, на который будет смотреть IntersectionObserver
 *
 * По умолчанию за экраном элементам ставится класс props.offsetClassName, при появлении - props.defaultClassName;
 * если хочется иного поведения для конкретного элемента, то ему нужно дополнительно выставить атрибуты:
 * - data-prx-offset-cn: класс ставится когда элемент не виден
 * - data-prx-default-cn: класс ставится когда элемент виден
 */

export const Parallaxer = (props: Props) => {
  useEffect(() => {
    if (!window.IntersectionObserver) {
      return;
    }
    let scrollDirection: 'up' | 'down' = 'down';
    let prevScrollY = 0;

    const scrollListener = () => {
      const currentScrollY = window.scrollY;
      if (prevScrollY > currentScrollY) {
        scrollDirection = 'up';
      } else {
        scrollDirection = 'down';
      }
      prevScrollY = currentScrollY;
    };

    window.addEventListener('scroll', scrollListener);

    const elementsToAnimate =
      document.querySelectorAll<HTMLElement>('[data-prx-by]');

    const observedIdToElementsMap: Record<string, Array<HTMLElement>> = {};
    for (const element of elementsToAnimate) {
      const targetId = element.dataset.prxBy || '';
      if (!targetId) {
        continue;
      }
      if (!observedIdToElementsMap[targetId]) {
        observedIdToElementsMap[targetId] = [];
      }
      observedIdToElementsMap[targetId].push(element);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const targetId = entry.target.id;
          const elements = observedIdToElementsMap[targetId];
          elements.forEach((element) => {
            const offsetClassName =
              element.dataset.prxOffsetCn || props.offsetClassName;
            const defaultClassName =
              element.dataset.prxDefaultCn || props.defaultClassName;
            const classList = element.classList;
            if (!classList.contains(props.initialClassName)) {
              classList.add(props.initialClassName);
              classList.add(
                entry.isIntersecting || entry.boundingClientRect.top < 0
                  ? defaultClassName
                  : offsetClassName,
              );
              return;
            }
            if (scrollDirection === 'up') {
              if (
                entry.intersectionRatio > 0.1 &&
                entry.boundingClientRect.top > 0
              ) {
                classList.remove(defaultClassName);
                classList.add(offsetClassName);
              }
            } else {
              if (entry.isIntersecting) {
                classList.add(defaultClassName);
                classList.remove(offsetClassName);
              }
            }
          });
        });
      },
      {threshold: [0.1, 0.7]},
    );

    Object.keys(observedIdToElementsMap).forEach((key) => {
      const element = document.getElementById(key);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      window.removeEventListener('scroll', scrollListener);
      observer.disconnect();
    };
  }, []);

  return null;
};
